import { useEffect, useRef, useState } from 'react';
import {
  CircularProgress,
  CircularProgressProps,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';

export interface CircularWithValueLabelProps<T> {
  loadingState: T;
}

function CircularProgressWithLabel(
  props: CircularProgressProps & { value: number },
) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant={'indeterminate'} {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

export const CircularWithValueLabel = ({
  loadingState,
}: CircularWithValueLabelProps<string>) => {
  const [progress, setProgress] = useState(0);
  const animationRef = useRef<number | null>(null);
  const startRef = useRef<number>(0);
  const curProgress = useRef<number>(0);

  useEffect(() => {
    const targetProgress = loadingState === 'FINISHING' ? 100 : 90;
    const totalLeft = targetProgress - progress;
    const duration = loadingState === 'FINISHING' ? 1000 : 2000;

    const updateProgress = (timestamp: number) => {
      if (startRef.current === 0) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const progressIncrement = (elapsed / duration) * totalLeft;

      if (curProgress.current < targetProgress) {
        curProgress.current = Math.min(
          curProgress.current + progressIncrement,
          targetProgress,
        );
        setProgress(curProgress.current);
        if (curProgress.current < targetProgress) {
          animationRef.current = requestAnimationFrame(updateProgress);
        }
      }
    };

    // Start animation
    animationRef.current = requestAnimationFrame(updateProgress);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      startRef.current = 0; // Reset start time on cleanup
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingState]);

  return <CircularProgressWithLabel value={progress} />;
};
